/* Carpano Kart — ligação WebSocket com o servidor de salas (server/index.js).
 * Só cuida da conexão: abrir, reabrir após queda e avisar quando desiste. O protocolo
 * fica no KartNet. Sem dependência de three/DOM (roda igual no navegador e no Node 22).
 *
 * Callbacks:
 *   onMsg(obj)        mensagem do servidor (já em objeto)
 *   onUnreachable()   nunca conseguiu abrir (servidor fora do ar / URL errada) → KartNet cai pro P2P
 *   onDrop()          caiu depois de conectado; vai tentar voltar
 *   onGiveUp()        não conseguiu voltar (passou da vaga guardada pelo servidor)
 *   makeResume()      mensagem para retomar a vaga após queda (ou null p/ não tentar) */
const CONNECT_TIMEOUT_MS = 4000;
const RETRY_DELAYS = [400, 800, 1200, 1600, 2000, 2400]; // ~8s no total; o servidor guarda a vaga por 12s

export class WsLink {
    constructor(url, cb) {
        this.url = url;
        this.cb = cb || {};
        this.ws = null;
        this.everOpen = false;
        this.closedByUs = false;
        this._first = null;
        this._try = 0;
        this._timer = null;
        this._connectTimer = null;
    }

    get open() { return !!this.ws && this.ws.readyState === 1; }

    connect(firstMsg) {
        this._first = firstMsg;
        this._dial();
    }

    _dial() {
        if (this.closedByUs) return;
        let ws;
        try { ws = new WebSocket(this.url); } catch (e) { this._failed(); return; }
        this.ws = ws;
        this._connectTimer = setTimeout(() => { try { ws.close(); } catch (_) { /* ok */ } this._failed(ws); }, CONNECT_TIMEOUT_MS);
        ws.onopen = () => {
            clearTimeout(this._connectTimer);
            this.everOpen = true;
            this._try = 0;
            const first = this._first;
            this._first = null;
            this.send(first || (this.cb.makeResume && this.cb.makeResume()));
        };
        ws.onmessage = (ev) => {
            let m; try { m = JSON.parse(ev.data); } catch (_) { return; }
            if (m && typeof m === 'object') this.cb.onMsg && this.cb.onMsg(m);
        };
        ws.onerror = () => { clearTimeout(this._connectTimer); this._failed(ws); }; // alguns ambientes só disparam error (sem close)
        ws.onclose = () => { clearTimeout(this._connectTimer); this._failed(ws); };
    }

    // chamado por onclose e pelo timeout de conexão; ignora sockets antigos
    _failed(ws) {
        if (ws && ws !== this.ws) return; // já tratado (error + close chegam os dois no navegador)
        if (this.closedByUs) return;
        if (ws) { ws.onclose = ws.onmessage = ws.onerror = ws.onopen = null; try { ws.close(); } catch (_) { /* ok */ } }
        this.ws = null;
        if (!this.everOpen) { this.cb.onUnreachable && this.cb.onUnreachable(); return; }
        const resume = this.cb.makeResume && this.cb.makeResume();
        if (!resume || this._try >= RETRY_DELAYS.length) { this.cb.onGiveUp && this.cb.onGiveUp(); return; }
        if (this._try === 0) this.cb.onDrop && this.cb.onDrop();
        this._first = resume;
        this._timer = setTimeout(() => this._dial(), RETRY_DELAYS[this._try++]);
    }

    send(m) {
        if (!m || !this.open) return false;
        try { this.ws.send(typeof m === 'string' ? m : JSON.stringify(m)); return true; } catch (_) { return false; }
    }

    close() {
        this.closedByUs = true;
        clearTimeout(this._timer);
        clearTimeout(this._connectTimer);
        const ws = this.ws;
        this.ws = null;
        if (ws) { ws.onclose = ws.onmessage = ws.onerror = ws.onopen = null; try { ws.close(); } catch (_) { /* ok */ } }
    }
}
